import { OrderPanelProps } from './index';
import React, { FC, memo, useEffect, useState } from 'react';
import { renderPromotionalText } from '@utils/Price.utils';
import { Icon, Icons } from '../../Icon';
import PriceSuffix from '../../PriceSuffix';
import IconButton from '../../IconButton';
import {
    BRAND,
    FINANCE_DETAIL_VIEW,
    isBrandDS,
    isBrandOV,
    isMarketGB,
    isMarketIT,
    ModalVersion,
    ROLES,
} from '../../../constants/main';
import FinanceWidget from '../../FinanceWidget';
import { DepositText } from '../../DepositText';
import { HandledComponentError } from '@components/ErrorHandling';
import { StyledGrid, ConfigMainGrid, ConfigLeftSection, ConfigRightSection } from '@components/Grid';
import { Link as LinkExtended, LinkColorType } from '@components/v2/link';
import Link from '@components/Link';
import { ContinueCTAButton } from '@components/ContinueCTAButton';
import { useOrdelPanelLogic } from '@hooks/useOrdelPanelLogic';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { theme } from '../../../styles/theme';
import Spinner from '@components/Spinner';
import { Pages } from '../../../types/pages';
import { ORDER_PANEL_ID, useOrderPanelHeight } from '@hooks/useOrderPanelHeight';
import { formatLeadTime } from '@utils/Date.utils';
import MonthlyPriceLabel, { AprSizes } from '@components/MonthlyPriceLabel';
import { useIsShowPriceExcludedVAT } from '@hooks/useIsShowPriceExcludedVAT';
import { PriceVatExcluded } from '@components/PriceVatExcluded';
import { useExperimentalFeatureFlagEnabled } from '@hooks/useFeatureSwitchEnabled';
import { EXPERIMENTAL_FEATURES_LIST } from '@utils/experiments/@types';
import { usePlausible, UserEvent } from '@hooks/usePlausible';
import { useRouter } from 'next/router';
import Routes from '../../../constants/routes';
import { Benefits } from '@components/Benefits';
import { buildParameterizedPathname, extractParamsFromPathname } from '@utils/url.utils';
import routes from '../../../constants/routes';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useGlobalDuck } from '@hooks/useGlobalDuck';
import Button from '@components/Button';

export const OrderPanelTemplate: FC<OrderPanelProps> = memo(
    ({ className, onSalesmanClick, onContinueBasketClick, onInfoIconClick, page }) => {
        try {
            const router = useRouter();

            const {
                t,
                formatMoney,
                filteredStockTrims,
                isPriceListLoading,
                carDetails,
                isStockJourney,
                isStockJourneyAllowed,
                isAllowedRedirectToStock,
                isAllowedRedirectToMTO,
                isCataloguePriceFeatureSwitchEnabled,
                isFinanceWidgetDisabled,
                isCustomDepositTextAllowed,
                processCalculateSummaryDataPromise,
                catalog,
                isBasketPage,
                showBasketLegalTerms,
                totalCashPrice,
                totalBasicPrice,
                totalBasicPriceWithoutVat,
                totalMonthlyPrice,
                deposit,
                apr,
                redirectToStock,
                scrollToSockSlice,
                redirectToMTO,
                handleFinanceWidgetBtnClick,
                registerRenderCb,
                dispatch,
                showSaveButton,
                isCataloguePriceEqualToStorePrice,
                hideCashPayment,
                infoIconType,
                amountRounding,
                financeDetailView,
                financeDetailText,
                IconName,
                offerValidityDateString,
                showFOLink,
                promoText,
            } = useOrdelPanelLogic();
            const isShowPriceExcludedVAT: boolean = useIsShowPriceExcludedVAT();
            const { plausibleUserEvent } = usePlausible();
            const { defaultConfiguration } = useCarDetailsDuck();
            const { carJourney } = useGlobalDuck();
            const [phoneVisible, setPhoneVisible] = useState(false);

            useOrderPanelHeight(carDetails?.extraFields?.promotionalText);

            useEffect(() => {
                const headerElement = document.getElementsByClassName('header-container')?.[0];

                window?.addEventListener('scroll', () => {
                    setPhoneVisible(window?.scrollY > headerElement.clientHeight);
                });
            }, []);

            const customizeClick = () => {
                const currentPathParams = extractParamsFromPathname(router.asPath);
                const newPathParams = {
                    nameplateBodyStyleSlug: currentPathParams.nameplateBodyStyleSlug,
                    speckPackSlug: carDetails?.specPackSlug,
                    engineGearboxFuelSlug: carDetails?.engineGearboxFuelSlug,
                    exteriorColourSlug: (defaultConfiguration || carDetails)?.exteriorColourSlug,
                    interiorColourSlug: (defaultConfiguration || carDetails)?.interiorColourSlug,
                };

                const modelURL = buildParameterizedPathname(routes.CAR, carJourney, newPathParams);

                return router.push(modelURL);
            };

            const contactClick = () => {
                dispatch(
                    UIDuck.openModal({
                        data: {},
                        modalType: UIDuck.MODAL_TYPES.NEED_HELP,
                        modalVersion: ModalVersion.v2,
                    })
                );
            };

            const Delivery = () => {
                return (
                    <div className="delivery-container">
                        <span className="delivery">{formatLeadTime(carDetails.leadTimeDate)}</span>
                        {isStockJourney
                            ? isAllowedRedirectToMTO && (
                                  <>
                                      <Link
                                          label={t('orderPanel.mtoRedirect.link')}
                                          onClick={redirectToMTO}
                                          marginTop={5}
                                          primary
                                          withArrowIcon
                                      />
                                  </>
                              )
                            : isAllowedRedirectToStock &&
                              isStockJourneyAllowed &&
                              filteredStockTrims.length > 0 && (
                                  <Link
                                      label={t('orderPanel.internalStockRedirect', {
                                          model: carDetails.model.title,
                                          count: filteredStockTrims.length,
                                      })}
                                      onClick={redirectToStock}
                                      primary
                                      withArrowIcon
                                  />
                              )}
                        {!isStockJourney && !isAllowedRedirectToStock && filteredStockTrims.length > 0 && (
                            <Link
                                label={t('orderPanel.internalStockRedirect', {
                                    model: carDetails.model.title,
                                    count: filteredStockTrims.length,
                                })}
                                onClick={scrollToSockSlice}
                                primary
                                withArrowIcon
                            />
                        )}
                    </div>
                );
            };

            const isContinueButtonHidden = useExperimentalFeatureFlagEnabled(
                EXPERIMENTAL_FEATURES_LIST.TRIM_PAGE_CONTINUE_STICKY
            );

            return (
                <div
                    className={`${className} ${isPriceListLoading ? 'isLoading' : ''} ${
                        isShowPriceExcludedVAT ? 'isShowPriceExcludedVAT-panel' : ''
                    }`}
                    id={ORDER_PANEL_ID}
                >
                    <StyledGrid brand={BRAND} noPadding isWide>
                        <ConfigMainGrid isWide>
                            <ConfigLeftSection>
                                <div className="wrapper">
                                    <div className="section">
                                        <div className="order-panel-price-container">
                                            <div className="content">
                                                {isPriceListLoading ? (
                                                    <Spinner size={20} border={3} color={theme.colors.black} />
                                                ) : (
                                                    <>
                                                        <span className="header">
                                                            {isCataloguePriceFeatureSwitchEnabled &&
                                                                (!isCataloguePriceEqualToStorePrice ||
                                                                    (hideCashPayment && !isMarketGB)) && (
                                                                    <>
                                                                        <span className="catalog-price-title">
                                                                            <span className="catalogPrice">
                                                                                {t('price.catalog.title')}&nbsp;
                                                                            </span>

                                                                            {formatMoney(
                                                                                catalog?.totalPrice !== undefined
                                                                                    ? catalog.totalPrice
                                                                                    : totalCashPrice,

                                                                                true
                                                                            )}
                                                                        </span>

                                                                        <span className="divider">|</span>
                                                                    </>
                                                                )}

                                                            <span className="heading">
                                                                {!isCataloguePriceEqualToStorePrice ? (
                                                                    <>
                                                                        {page !== Pages.trim ? (
                                                                            <span>
                                                                                {t('offerListItem.specialPriceText')}
                                                                            </span>
                                                                        ) : (
                                                                            <span>
                                                                                {t(
                                                                                    'offerListItem.specialPriceTextFrom'
                                                                                )}
                                                                            </span>
                                                                        )}

                                                                        {page !== Pages.trim &&
                                                                            isBrandDS &&
                                                                            !isMarketGB && (
                                                                                <span className="priceFromText">
                                                                                    {t('nameplate.priceFrom')}
                                                                                </span>
                                                                            )}
                                                                    </>
                                                                ) : (
                                                                    t('offerListItem.standardPriceText')
                                                                )}
                                                            </span>
                                                        </span>

                                                        <div className="body">
                                                            <div className="body-wrapper">
                                                                {!hideCashPayment && (
                                                                    <span
                                                                        className="price"
                                                                        data-testid={
                                                                            router.pathname.includes(Routes.SELECTOR)
                                                                                ? 'TESTING_SELECTOR_CASH_PRICE'
                                                                                : 'TESTING_CONFIG_CASH_PRICE'
                                                                        }
                                                                    >
                                                                        {formatMoney(
                                                                            carDetails?.stock
                                                                                ? totalBasicPrice
                                                                                : totalCashPrice,
                                                                            true
                                                                        )}
                                                                        <PriceSuffix
                                                                            showOnlyPrioritizedTranslation
                                                                            checkPrioritizedTranslation
                                                                        />

                                                                        {isMarketGB && (
                                                                            <span className="priceOtr">
                                                                                {t('price.OTR')}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                )}

                                                                {isShowPriceExcludedVAT && (
                                                                    <span className="price isShowPriceExcludedVAT-price">
                                                                        <PriceVatExcluded
                                                                            price={formatMoney(
                                                                                totalBasicPriceWithoutVat,
                                                                                true
                                                                            )}
                                                                        />
                                                                    </span>
                                                                )}

                                                                {!isShowPriceExcludedVAT &&
                                                                    !hideCashPayment &&
                                                                    totalMonthlyPrice && (
                                                                        <span className="or">
                                                                            {t('configurator.optionPrice.suffix')}
                                                                        </span>
                                                                    )}

                                                                {!isShowPriceExcludedVAT && totalMonthlyPrice && (
                                                                    <>
                                                                        <div>
                                                                            <div
                                                                                className="totalMonthlyPriceWrapper"
                                                                                data-testid={
                                                                                    router.pathname.includes(
                                                                                        Routes.SELECTOR
                                                                                    )
                                                                                        ? 'TESTING_SELECTOR_FINANCE_PRICE'
                                                                                        : 'TESTING_CONFIG_FINANCE_PRICE'
                                                                                }
                                                                            >
                                                                                <span className="price">
                                                                                    {formatMoney(
                                                                                        totalMonthlyPrice,
                                                                                        false,
                                                                                        '',
                                                                                        amountRounding
                                                                                    )}
                                                                                </span>

                                                                                <span className="prefix">
                                                                                    <PriceSuffix
                                                                                        checkPrioritizedTranslation
                                                                                        isMonthlyPrice
                                                                                    />
                                                                                </span>

                                                                                {financeDetailView ===
                                                                                    FINANCE_DETAIL_VIEW.ICON && (
                                                                                    <IconButton
                                                                                        className="infoIcon"
                                                                                        icon={infoIconType}
                                                                                        dataTestId={
                                                                                            router.pathname.includes(
                                                                                                Routes.SELECTOR
                                                                                            )
                                                                                                ? 'TESTING_SELECTOR_FINANCE_PRICE_INFO_ICON'
                                                                                                : 'TESTING_CONFIG_FINANCE_PRICE_INFO_ICON'
                                                                                        }
                                                                                        width={16}
                                                                                        height={16}
                                                                                        onClick={() => {
                                                                                            processCalculateSummaryDataPromise();
                                                                                            onInfoIconClick(
                                                                                                financeDetailText
                                                                                            );
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </div>

                                                                            <span
                                                                                className={`footer ${
                                                                                    financeDetailView ===
                                                                                        FINANCE_DETAIL_VIEW.ICON &&
                                                                                    'footer-icon-displayed'
                                                                                }`}
                                                                            >
                                                                                {isCustomDepositTextAllowed ? (
                                                                                    <DepositText />
                                                                                ) : (
                                                                                    <>
                                                                                        {!isMarketGB &&
                                                                                            renderPromotionalText(
                                                                                                promoText,

                                                                                                isMarketIT && isBrandOV
                                                                                                    ? t(
                                                                                                          'price.pattern.anticipo'
                                                                                                      )
                                                                                                    : t(
                                                                                                          'price.pattern'
                                                                                                      ),

                                                                                                deposit,
                                                                                                null,
                                                                                                null,
                                                                                                null,
                                                                                                offerValidityDateString,
                                                                                                { apr }
                                                                                            )}
                                                                                        {showFOLink && (
                                                                                            <MonthlyPriceLabel
                                                                                                withPrimaryColor={
                                                                                                    isMarketGB
                                                                                                }
                                                                                                withoutMarginTop
                                                                                                size={AprSizes.SMALL}
                                                                                                aprValue={apr}
                                                                                                aprCustomTextKey={
                                                                                                    promoText
                                                                                                }
                                                                                                calculateSummaryData={
                                                                                                    processCalculateSummaryDataPromise
                                                                                                }
                                                                                                financeDetailText={
                                                                                                    financeDetailText
                                                                                                }
                                                                                            />
                                                                                        )}
                                                                                    </>
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            {router.pathname.includes(Routes.CAR) &&
                                                !isFinanceWidgetDisabled &&
                                                totalMonthlyPrice && (
                                                    <>
                                                        <LinkExtended
                                                            isUnderline
                                                            className="calculator"
                                                            iconWidth={40}
                                                            iconHeight={40}
                                                            iconColor={LinkColorType.BLACK}
                                                            iconHoverColor={LinkColorType.BLACK}
                                                            disableIconStroke
                                                            isReversed
                                                            iconName={Icons.Calculator}
                                                            iconAltTxt={t('basket.paymentDetails.personalizePayment')}
                                                            iconAltId="personalizeFinanceDetailsLink"
                                                            iconClassName="chevron-right-thin"
                                                            txt={
                                                                <span className="txt">
                                                                    <span>{t('configurator.personalize')}</span>

                                                                    <span>{t('configurator.financing')}</span>
                                                                </span>
                                                            }
                                                            onClick={handleFinanceWidgetBtnClick}
                                                        >
                                                            <button
                                                                className="calculator"
                                                                onClick={handleFinanceWidgetBtnClick}
                                                                data-testid="TESTING_CONFIG_FINANCE_PRICE_CALCULATOR"
                                                            >
                                                                <Icon
                                                                    name={
                                                                        isBrandDS
                                                                            ? Icons.CalculatorDS
                                                                            : Icons.Calculator
                                                                    }
                                                                    width={40}
                                                                    height={40}
                                                                    altText={t(
                                                                        'basket.paymentDetails.personalizePayment'
                                                                    )}
                                                                    altId="personalizeFinanceDetailsLink"
                                                                    className="financeContainer-price-calculatorIcon"
                                                                    role={ROLES.IMG}
                                                                />

                                                                <span className="txt">
                                                                    <span>{t('configurator.personalize')}</span>

                                                                    <span>{t('configurator.financing')}</span>
                                                                </span>
                                                            </button>
                                                        </LinkExtended>
                                                    </>
                                                )}
                                        </div>
                                    </div>
                                    <div className="section section-middle">
                                        <div className="section-middle-wrapper benefitSection">
                                            {router.pathname.includes(Routes.SELECTOR) ? (
                                                <Benefits offers={carDetails?.offers} />
                                            ) : (
                                                <Delivery />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </ConfigLeftSection>
                            <ConfigRightSection unsetMaxWidth>
                                <div className="wrapper">
                                    <div className="section section-end">
                                        <div className="desktopContactSection">
                                            <Link
                                                label={t('configurator.contactSalesman')}
                                                onClick={() => {
                                                    plausibleUserEvent(UserEvent.HelpNeeded);
                                                    dispatch(
                                                        UIDuck.openModal({
                                                            data: {},
                                                            modalType: UIDuck.MODAL_TYPES.NEED_HELP,
                                                            modalVersion: ModalVersion.v2,
                                                        })
                                                    );
                                                }}
                                                primary
                                                withArrowIcon
                                                data-testid={
                                                    router.pathname.includes(Routes.SELECTOR)
                                                        ? 'TESTING_SELECTOR_CONTACT_SALESMAN_LINK'
                                                        : 'TESTING_CONFIG_CONTACT_SALESMAN_LINK'
                                                }
                                            />
                                            {phoneVisible && (
                                                <a href={`tel:${t('support.telephone')}`} className="phoneLink">
                                                    {t('support.telephone')}
                                                </a>
                                            )}
                                        </div>

                                        {!isContinueButtonHidden && (!isBasketPage || !showBasketLegalTerms) && (
                                            <div className="save-to-continue">
                                                {router.pathname.includes(Routes.CAR) ? (
                                                    <ContinueCTAButton
                                                        page={page}
                                                        dataId="TESTING_TO_BASKET_BOX_ORDERPANEL"
                                                        fullWidth
                                                        translationKey="configurator.interestedBox1.link.finance.v2"
                                                        htmlId="TESTING_TO_BASKET_BOX_ORDERPANEL"
                                                        position="sticky"
                                                    />
                                                ) : (
                                                    <Button
                                                        onClick={() => {
                                                            customizeClick();
                                                            if (onContinueBasketClick) onContinueBasketClick();
                                                        }}
                                                        primary
                                                        data-testid="TESTING_TO_CONFIG_BOX_ORDERPANEL"
                                                    >
                                                        {t('orderPanel.button.label.basket')}
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </ConfigRightSection>
                        </ConfigMainGrid>
                    </StyledGrid>
                    <div className="mobileContactSection" onClick={contactClick}>
                        <span className="label">
                            {t('header.nav.needHelp')} <Icon name={Icons.ChevronRight} width={11} height={11} />
                        </span>
                        <span className="phone">
                            <Icon
                                name={isBrandOV && !isMarketGB ? Icons.PhoneV2 : Icons.Phone}
                                width={24}
                                height={24}
                            />
                            {t('support.telephone')}
                        </span>
                    </div>
                    {!isFinanceWidgetDisabled && <FinanceWidget registerRenderCb={registerRenderCb} />}
                </div>
            );
        } catch (e: any) {
            return <HandledComponentError error={e} />;
        }
    }
);
OrderPanelTemplate.displayName = 'OrderPanelTemplate';
export default OrderPanelTemplate;
